"use client"

import { useState, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Copy, Hash, ShieldCheck } from "lucide-react"
import { toast } from "sonner"

type Algorithm = "SHA-1" | "SHA-256" | "SHA-384" | "SHA-512"

const ALGORITHMS: Algorithm[] = ["SHA-1", "SHA-256", "SHA-384", "SHA-512"]

interface HashResult {
  algorithm: Algorithm
  hash: string
}

export function HashGenerator() {
  const [input, setInput] = useState("")
  const [inputType, setInputType] = useState<"text" | "file">("text")
  const [algorithm, setAlgorithm] = useState<Algorithm>("SHA-256")
  const [results, setResults] = useState<HashResult[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [compareHash, setCompareHash] = useState("")
  const [hmacEnabled, setHmacEnabled] = useState(false)
  const [hmacKey, setHmacKey] = useState("")

  const generateHash = useCallback(async (data: ArrayBuffer, algo: Algorithm): Promise<string> => {
    const hashBuffer = await crypto.subtle.digest(algo, data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("")
  }, [])

  const generateHmac = useCallback(async (data: ArrayBuffer, algo: Algorithm, keyStr: string): Promise<string> => {
    const encoder = new TextEncoder()
    const keyData = encoder.encode(keyStr)

    const cryptoKey = await crypto.subtle.importKey(
      "raw",
      keyData,
      { name: "HMAC", hash: algo },
      false,
      ["sign"]
    )

    const signature = await crypto.subtle.sign("HMAC", cryptoKey, data)
    const hashArray = Array.from(new Uint8Array(signature))
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("")
  }, [])

  const hashText = useCallback(async () => {
    if (!input.trim()) {
      toast.error("Please enter some text to hash")
      return
    }

    if (hmacEnabled && !hmacKey) {
      toast.error("Please enter an HMAC key")
      return
    }

    setIsProcessing(true)
    try {
      const encoder = new TextEncoder()
      const data = encoder.encode(input).buffer as ArrayBuffer

      const newResults: HashResult[] = await Promise.all(
        ALGORITHMS.map(async (algo) => ({
          algorithm: algo,
          hash: hmacEnabled
            ? await generateHmac(data, algo, hmacKey)
            : await generateHash(data, algo),
        }))
      )

      setResults(newResults)
    } catch (error) {
      toast.error("Failed to generate hash")
      console.error(error)
    } finally {
      setIsProcessing(false)
    }
  }, [input, hmacEnabled, hmacKey, generateHash, generateHmac])

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      if (hmacEnabled && !hmacKey) {
        toast.error("Please enter an HMAC key")
        return
      }

      setIsProcessing(true)
      try {
        const arrayBuffer = await file.arrayBuffer()

        const newResults: HashResult[] = await Promise.all(
          ALGORITHMS.map(async (algo) => ({
            algorithm: algo,
            hash: hmacEnabled
              ? await generateHmac(arrayBuffer, algo, hmacKey)
              : await generateHash(arrayBuffer, algo),
          }))
        )

        setResults(newResults)
        toast.success(`Hashed file: ${file.name}`)
      } catch (error) {
        toast.error("Failed to hash file")
        console.error(error)
      } finally {
        setIsProcessing(false)
      }
    },
    [hmacEnabled, hmacKey, generateHash, generateHmac]
  )

  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text)
    toast.success("Copied to clipboard!")
  }, [])

  const selectedResult = results.find((r) => r.algorithm === algorithm)
  const hashMatches =
    compareHash && selectedResult
      ? compareHash.toLowerCase() === selectedResult.hash.toLowerCase()
      : null

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Generate Hash</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs value={inputType} onValueChange={(v) => setInputType(v as "text" | "file")}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="text">Text Input</TabsTrigger>
              <TabsTrigger value="file">File Input</TabsTrigger>
            </TabsList>
            <TabsContent value="text" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="input">Text to hash</Label>
                <Textarea
                  id="input"
                  placeholder="Enter text to generate hash..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  className="min-h-[100px] font-mono"
                />
              </div>
              <Button onClick={hashText} disabled={isProcessing} className="w-full">
                <Hash className="w-4 h-4 mr-2" />
                {isProcessing ? "Processing..." : hmacEnabled ? "Generate HMAC" : "Generate Hashes"}
              </Button>
            </TabsContent>
            <TabsContent value="file" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="file">Select file</Label>
                <Input
                  id="file"
                  type="file"
                  onChange={handleFileChange}
                  disabled={isProcessing}
                />
                <p className="text-sm text-muted-foreground">
                  File is processed locally in your browser. Nothing is uploaded.
                </p>
              </div>
            </TabsContent>
          </Tabs>

          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <Label htmlFor="hmac-toggle" className="font-medium">HMAC Mode</Label>
              </div>
              <Switch
                id="hmac-toggle"
                checked={hmacEnabled}
                onCheckedChange={setHmacEnabled}
              />
            </div>
            {hmacEnabled && (
              <div className="space-y-2">
                <Label htmlFor="hmac-key">Secret Key</Label>
                <Input
                  id="hmac-key"
                  type="password"
                  placeholder="Enter HMAC secret key..."
                  value={hmacKey}
                  onChange={(e) => setHmacKey(e.target.value)}
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">
                  HMAC provides message authentication using a secret key combined with the hash algorithm.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{hmacEnabled ? "HMAC Results" : "Hash Results"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {results.map((result) => (
              <div key={result.algorithm} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">
                    {hmacEnabled ? `HMAC-${result.algorithm}` : result.algorithm}
                  </Label>
                  <span className="text-xs text-muted-foreground">
                    {result.hash.length * 4} bits
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    value={result.hash}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(result.hash)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Verify Hash</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="w-full sm:w-32">
                <Label>Algorithm</Label>
                <Select value={algorithm} onValueChange={(v) => setAlgorithm(v as Algorithm)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ALGORITHMS.map((algo) => (
                      <SelectItem key={algo} value={algo}>
                        {algo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label>Hash to compare</Label>
                <Input
                  placeholder="Paste hash to verify..."
                  value={compareHash}
                  onChange={(e) => setCompareHash(e.target.value)}
                  className={`font-mono ${
                    hashMatches === true
                      ? "border-green-500 focus-visible:ring-green-500"
                      : hashMatches === false
                      ? "border-red-500 focus-visible:ring-red-500"
                      : ""
                  }`}
                />
              </div>
            </div>
            {hashMatches !== null && (
              <p
                className={`text-sm font-medium ${
                  hashMatches ? "text-green-600" : "text-red-600"
                }`}
              >
                {hashMatches ? "Hashes match!" : "Hashes do not match"}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
